// Concerts-specific API base URL. Set `NEXT_PUBLIC_CONCERTS_API_URL` in
// convenience during initial local dev where the two services might
// share a host. Explicit is better — set the concerts var separately
// once the concerts service has its own host.
export const CONCERTS_API_BASE_URL = process.env.NEXT_PUBLIC_CONCERTS_API_URL ?? '';

/**
 * Prefix for static images in public/ folder.
 * In deployed environments, CloudFront only routes concerts/* to the
 * frontend host, so images must be served from /concerts/image/*
 * instead of /image/*. The rewrite in next.config.js maps
 * /concerts/image/* back to /image/* on the server.
 */
export const IMG_PREFIX = process.env.NODE_ENV === 'development' ? '' : '/concerts';

type DateRangeKey = 'today' | 'next7days' | 'thisWeek' | 'thisMonth' | undefined;

interface DateFilterInput {
  dateRange?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Build dateFrom/dateTo based on a high-level dateRange key plus any explicit dates.
 * - today / thisWeek / thisMonth override explicit dates
 * - anything else just passes through dateFrom/dateTo
 *
 * Same shape and semantics as the concert twin — kept here rather than
 * imported so concerts frontend has zero cross-imports from concert.
 */
export function buildDateFilters(input: DateFilterInput): { dateFrom?: string; dateTo?: string } {
  const { dateRange, dateFrom, dateTo } = input;
  const key = (dateRange as DateRangeKey) || undefined;

  const format = (d: Date) =>
    new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);

  const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

  if (!key) {
    return { dateFrom, dateTo };
  }

  const today = new Date();

  switch (key) {
    case 'today': {
      const value = format(today);
      return { dateFrom: value, dateTo: value };
    }
    case 'next7days':
    case 'thisWeek': {
      const from = format(today);
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + 7);
      const to = format(toDate);
      return { dateFrom: from, dateTo: to };
    }
    case 'thisMonth': {
      const from = format(today);
      const to = format(endOfMonth(today));
      return { dateFrom: from, dateTo: to };
    }
    default:
      return { dateFrom, dateTo };
  }
}
